import { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Loader2, Smile } from 'lucide-react';
import { BaseModal, useModalClose } from '@/components/modal';
import { EmojiPicker } from '@/features/messaging/components/EmojiPicker';
import { useComments, useAddComment, useReplies, useEditComment, useDeleteComment } from '../hooks';
import { CommentItem } from './CommentItem';
import type { PostComment } from '../types';

function CommentsSheetHeader({ onClose, commentCount }: { onClose: () => void; commentCount: number }) {
  const { t } = useTranslation();
  const animatedClose = useModalClose();
  const handleClose = animatedClose || onClose;
  return (
    <div className="flex-shrink-0">
      <div className="flex justify-center pt-2 pb-1 md:hidden">
        <div className="w-12 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }} />
      </div>
      <div className="flex items-center justify-between px-6 py-2.5 border-b border-gray-200">
        <h2 className="font-semibold text-xs sm:text-sm text-neutral-800">
          {t('feed.comments_with_count', { count: commentCount })}
        </h2>
        <button
          onClick={handleClose}
          className="p-1.5 rounded-lg transition-all active:scale-90 text-neutral-500 border border-gray-200 bg-white"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface CommentsSheetProps {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
  commentCount: number;
}

export function CommentsSheet({
  postId,
  isOpen,
  onClose,
  commentCount,
}: CommentsSheetProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [replyTo, setReplyTo] = useState<PostComment | null>(null);
  const [editingComment, setEditingComment] = useState<PostComment | null>(null);
  const [loadedReplies, setLoadedReplies] = useState<Set<string>>(new Set());
  const [emojiOpen, setEmojiOpen] = useState(false);

  const {
    data: commentsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useComments(isOpen ? postId : null);

  const addComment = useAddComment();
  const editComment = useEditComment();
  const deleteComment = useDeleteComment();

  const comments = commentsData?.pages.flatMap((p) => p.comments) ?? [];

  // Focus input when replying
  useEffect(() => {
    if (replyTo || editingComment) {
      inputRef.current?.focus();
    }
  }, [replyTo, editingComment]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setReplyTo(null);
      setEditingComment(null);
      setInputValue('');
      setLoadedReplies(new Set());
      setEmojiOpen(false);
    }
  }, [isOpen]);

  // Populate input when editing
  useEffect(() => {
    if (editingComment) {
      setInputValue(editingComment.content);
    }
  }, [editingComment]);

  const handleSubmit = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;

    try {
      if (editingComment) {
        await editComment.mutateAsync({
          postId,
          commentId: editingComment.id,
          content: text,
        });
        setEditingComment(null);
      } else {
        await addComment.mutateAsync({
          postId,
          data: {
            content: text,
            ...(replyTo ? { parentId: replyTo.id } : {}),
          },
        });
        setReplyTo(null);
      }
      setInputValue('');
    } catch {
      // Error handled by react-query
    }
  }, [inputValue, postId, replyTo, editingComment, addComment, editComment]);

  const handleReply = useCallback((comment: PostComment) => {
    setEditingComment(null);
    setReplyTo(comment);
    setInputValue('');
  }, []);

  const handleEdit = useCallback((comment: PostComment) => {
    setReplyTo(null);
    setEditingComment(comment);
  }, []);

  const handleDelete = useCallback(
    (commentId: string) => {
      deleteComment.mutate({ postId, commentId });
    },
    [deleteComment, postId],
  );

  const handleShowReplies = useCallback((commentId: string) => {
    setLoadedReplies((prev) => new Set(prev).add(commentId));
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    setInputValue((prev) => prev + emoji);
    setEmojiOpen(false);
    inputRef.current?.focus();
  }, []);

  const isSubmitting = addComment.isPending || editComment.isPending;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      mobileVariant="bottomsheet"
      maxWidth="max-w-md"
      ariaLabel={t('feed.comments_title')}
    >
      <CommentsSheetHeader onClose={onClose} commentCount={commentCount} />

      {/* Comments list */}
      <div
        className="flex-1 overflow-y-auto px-4 py-2"
        style={{
          maxHeight: '60dvh',
          minHeight: '200px',
          overscrollBehaviorY: 'contain',
        }}
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--color-primary-500)' }} />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-neutral-500">
              {t('feed.no_comments')}
            </p>
          </div>
        ) : (
          <>
            {comments.map((comment) => (
              <Fragment key={comment.id}>
                <CommentItem
                  comment={comment}
                  onReply={handleReply}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onShowReplies={handleShowReplies}
                />
                {loadedReplies.has(comment.id) && (
                  <RepliesSection
                    postId={postId}
                    commentId={comment.id}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                )}
              </Fragment>
            ))}

            {/* Load more comments */}
            {hasNextPage && (
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-xs font-bold rounded-lg mt-2 transition-all active:scale-[0.98] disabled:opacity-50 bg-neutral-50 border border-gray-200"
                style={{ color: 'var(--color-primary-500)' }}
              >
                {isFetchingNextPage ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  t('feed.load_more_comments')
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Reply-to indicator */}
      {(replyTo || editingComment) && (
        <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 border-t border-gray-200">
          <span className="text-xs font-medium text-neutral-800 truncate">
            {editingComment
              ? t('feed.editing_comment')
              : t('feed.replying_to', {
                  name: `${replyTo?.author.firstName ?? ''} ${replyTo?.author.lastName ?? ''}`.trim(),
                })}
          </span>
          <button
            onClick={() => {
              setReplyTo(null);
              setEditingComment(null);
              setInputValue('');
            }}
            className="p-1 rounded transition-all active:scale-90 text-neutral-500"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Emoji picker */}
      <EmojiPicker
        isOpen={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        onEmojiSelect={handleEmojiSelect}
        bottomOffset={60}
      />

      {/* Input bar */}
      <div
        className="flex items-center gap-2 px-4 py-3 flex-shrink-0 bg-white border-t border-gray-200"
        style={{
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
        }}
      >
        <button
          onClick={() => setEmojiOpen((v) => !v)}
          className="p-2 rounded-xl transition-all active:scale-90 text-neutral-500"
          aria-label="Emoji"
        >
          <Smile className="h-5 w-5" />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          onFocus={() => setEmojiOpen(false)}
          placeholder={t('feed.comment_placeholder')}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm font-medium text-neutral-800 outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-white border border-gray-200"
          dir="auto"
          disabled={isSubmitting}
        />
        <button
          onClick={handleSubmit}
          disabled={!inputValue.trim() || isSubmitting}
          className="p-2.5 rounded-xl transition-all active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed text-white border border-gray-200"
          style={{
            backgroundColor: 'var(--color-primary-500)',
            boxShadow:
              inputValue.trim() && !isSubmitting ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
          aria-label={t('feed.send_comment')}
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </BaseModal>
  );
}

/** Sub-component to load replies for a specific comment */
function RepliesSection({
  postId,
  commentId,
  onReply,
  onEdit,
  onDelete,
}: {
  postId: string;
  commentId: string;
  onReply: (c: PostComment) => void;
  onEdit: (c: PostComment) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useReplies(postId, commentId);

  const replies = data?.pages.flatMap((p) => p.replies) ?? [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-2 ps-8">
        <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--color-primary-500)' }} />
      </div>
    );
  }

  return (
    <div className="ps-4">
      {replies.map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          depth={1}
        />
      ))}
      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="text-[11px] font-bold ps-8 py-1 transition-colors hover:opacity-80"
          style={{ color: 'var(--color-primary-500)' }}
        >
          {isFetchingNextPage ? (
            <Loader2 className="h-3 w-3 animate-spin inline" />
          ) : (
            t('feed.load_more_replies')
          )}
        </button>
      )}
    </div>
  );
}
